import { Button, message } from 'antd'
import React, { FunctionComponent, useCallback, useMemo } from 'react'

import { useCreateAuctionPropertyMutation } from '../../../graphql/generated'
import {
  DRAWER_TYPE,
  DRAWER_VIEW,
  resetPropertyDrawer,
  setDrawerView,
} from '../../../redux/drawerSlice'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { usePropertyDetailTitle } from '../../utils/usePropertyDetailTitle'
import { AuctionDetailDrawerFooterAssigning } from '../AuctionDetailDrawerFooterAssigning/AuctionDetailDrawerFooterAssigning'
import { AuctionDetailDrawerFooterWrapper } from '../AuctionDetailDrawerFooterWrapper/AuctionDetailDrawerFooterWrapper'
import { AuctionMonitoringLinkedPropertyHelpModalButton } from '../AuctionMonitoringLinkedPropertyHelpModalButton/AuctionMonitoringLinkedPropertyHelpModalButton'

export interface AuctionDetailDrawerFooterProps {
  auctionId?: string
  linkedPropertiesIds: string[]
}

export const AuctionDetailDrawerFooter: FunctionComponent<AuctionDetailDrawerFooterProps> =
  ({ auctionId, linkedPropertiesIds }) => {
    const dispatch = useAppDispatch()
    const [, createAuctionProperty] = useCreateAuctionPropertyMutation()
    const { userId, tenantId } = useAppSelector(state => state.myProfile)
    const ruianData = useAppSelector(state => state.property.ruian.data)
    const drawerView = useAppSelector(state => state.drawer?.viewType)
    const propertyName = usePropertyDetailTitle()

    const isAssignmentMode = drawerView === DRAWER_VIEW.ASSIGNMENT_MODE
    const selectedPropertyId = useAppSelector(
      state => state.drawer?.[DRAWER_TYPE.PROPERTY]?.id
    )
    const isAssignmentReady =
      Boolean(ruianData?.id) && selectedPropertyId === ruianData?.id

    const isAlreadyAssigned = useMemo(
      () =>
        Boolean(ruianData?.id && linkedPropertiesIds.includes(ruianData.id)),
      [linkedPropertiesIds, ruianData?.id]
    )

    const startAssignmentMode = useCallback(() => {
      dispatch(setDrawerView(DRAWER_VIEW.ASSIGNMENT_MODE))
    }, [dispatch])

    const handleAssign = useCallback(async () => {
      if (!ruianData?.id || !ruianData?.type || !auctionId) {
        message.error('Chyba při přiřazení nemovitosti.')
        return
      }
      try {
        await createAuctionProperty({
          auctionProperty: {
            propertyId: ruianData.id,
            propertyType: ruianData.type,
            auctionId,
            propertyName,
            accountUserId: userId,
            accountId: tenantId,
          },
        })
        message.success('Nemovitost byla přiřazena.')
        resetPropertyDrawer()
      } catch (error) {
        message.error('Chyba při přiřazení nemovitosti.')
      }
    }, [
      ruianData,
      auctionId,
      createAuctionProperty,
      propertyName,
      userId,
      tenantId,
    ])

    if (!auctionId) return null

    return (
      <AuctionDetailDrawerFooterWrapper
        isAssignmentMode={isAssignmentMode}
        isAssignmentReady={isAssignmentReady}
        isAlreadyAssigned={isAlreadyAssigned}
      >
        {isAssignmentMode ? (
          <AuctionDetailDrawerFooterAssigning
            propertyName={propertyName}
            isAssignmentReady={isAssignmentReady}
            isAlreadyAssigned={isAlreadyAssigned}
            handleAssign={handleAssign}
          />
        ) : (
          <div className="flex w-full justify-end">
            <AuctionMonitoringLinkedPropertyHelpModalButton defaultActiveKey="map" />
            <Button type="primary" onClick={startAssignmentMode}>
              Přiřadit nemovitosti k dražbě
            </Button>
          </div>
        )}
      </AuctionDetailDrawerFooterWrapper>
    )
  }
